import { useMemo, type JSX, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useMatch, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DiscoveryClientProvider } from './api/context';
import { useFolders } from './hooks/folders';
import { FolderIndexRedirect, FolderRoute, FoldersHome } from './screens/FoldersScreen';
import { CreatorsScreen } from './screens/CreatorsScreen';
import { EmailScreen } from './email/EmailScreen';
import { EmailCampaignScreen } from './email/EmailCampaignScreen';
import { RepeatRunsScreen } from './screens/RepeatRunsScreen';
import { SuggestionsPanel } from './screens/SuggestionsPanel';
import { OutreachScreen } from './screens/OutreachScreen';
import SettingsScreen from './screens/SettingsScreen';
import { Sidebar } from './screens/Sidebar';
import {
  DEFAULT_DISCOVERY_BASE,
  DEFAULT_EMAIL_BASE,
  DiscoveryBaseContext,
  EmailBaseContext,
  buildPaths,
  useDiscoveryPaths,
} from './workspace/paths';
import type { DiscoveryAppProps, EmailAppProps } from './types';

/**
 * Mountable root for the discovery UI. Must sit under a react-router route
 * ending in `/*` (e.g. `discovery/*`) — every screen has a URL below
 * `basePath`, so a refresh keeps your place and links can be shared.
 *
 * Styling follows the host portal's tokens, so the tool reads as part of the
 * same product and inherits light/dark mode for free.
 */
export function DiscoveryApp(props: DiscoveryAppProps): JSX.Element {
  return (
    <DiscoveryProviders {...props}>
      <DiscoveryShell {...props} />
    </DiscoveryProviders>
  );
}

/**
 * The Email page — its own entry in the portal's Sourcing nav, mounted apart
 * from Discovery (e.g. at `email/*`) but on the same backend. Links into
 * Discovery (a creator's folder) still resolve against `basePath`.
 */
export function EmailApp(props: EmailAppProps): JSX.Element {
  return (
    <DiscoveryProviders {...props}>
      <Routes>
        <Route index element={<EmailScreen />} />
        <Route path="campaigns/:campaignId" element={<EmailCampaignScreen />} />
        <Route path="*" element={<Navigate to={props.emailBasePath ?? DEFAULT_EMAIL_BASE} replace />} />
      </Routes>
    </DiscoveryProviders>
  );
}

function DiscoveryProviders({ children, ...props }: EmailAppProps & { children: ReactNode }): JSX.Element {
  // Own QueryClient on purpose: cache isolation from the host portal.
  const queryClient = useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
        },
      }),
    [],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <DiscoveryClientProvider apiUrl={props.apiUrl} getToken={props.getToken}>
        <DiscoveryBaseContext.Provider value={props.basePath ?? DEFAULT_DISCOVERY_BASE}>
          <EmailBaseContext.Provider value={props.emailBasePath ?? DEFAULT_EMAIL_BASE}>
            {children}
          </EmailBaseContext.Provider>
        </DiscoveryBaseContext.Provider>
      </DiscoveryClientProvider>
    </QueryClientProvider>
  );
}

function DiscoveryShell({ role, features, basePath, emailBasePath, currentUserId }: DiscoveryAppProps): JSX.Element {
  const outreachEnabled = features?.outreach ?? false;
  const paths = buildPaths(basePath ?? DEFAULT_DISCOVERY_BASE, emailBasePath ?? DEFAULT_EMAIL_BASE);
  // ONE folders query for the whole app (the same key FolderRoute reads), so
  // a folder created server-side shows in the sidebar and resolves together.
  const { data: folders } = useFolders({ includeArchived: true });
  const match = useMatch(`${paths.base}/folders/:folderId/*`);
  const selectedFolderId = match?.params.folderId ?? null;

  return (
    <div className="flex h-full min-h-0 bg-background text-foreground" data-discovery-role={role}>
      <Sidebar
        folders={folders ?? []}
        selectedFolderId={selectedFolderId}
        outreachEnabled={outreachEnabled}
        currentUserId={currentUserId}
      />
      <main className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-6">
          <Routes>
            <Route index element={<FoldersHome />} />
            <Route path="folders/:folderId" element={<FolderIndexRedirect />} />
            <Route path="folders/:folderId/:tab" element={<FolderRoute />} />
            <Route path="creators" element={<CreatorsScreen />} />
            {/* Email moved to its own page; old links land there. */}
            <Route path="email" element={<MovedToEmail to={paths.email()} />} />
            <Route path="email/campaigns/:campaignId" element={<MovedCampaign />} />
            <Route path="repeat-runs" element={<RepeatRunsScreen />} />
            <Route path="suggestions" element={<SuggestionsPanel />} />
            <Route path="settings" element={<SettingsScreen />} />
            {outreachEnabled && <Route path="outreach" element={<OutreachScreen />} />}
            <Route path="*" element={<Navigate to={paths.home} replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function MovedToEmail({ to }: { to: string }): JSX.Element {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function MovedCampaign(): JSX.Element {
  const paths = useDiscoveryPaths();
  const { campaignId = '' } = useParams();
  return <Navigate to={paths.emailCampaign(campaignId)} replace />;
}
